import { useEffect, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import type { Role } from '@eprintsmart/shared';
import { auth } from './firebase';

/**
 * Gates the console on a role claim.
 *
 * The claim is read from the ID token, not from a Firestore document, so a
 * user cannot grant themselves access by writing to their own profile. This is
 * a convenience check only — Firestore rules enforce the same claim server-side,
 * and every privileged mutation goes through a Cloud Function that re-checks it.
 */

interface Props {
  requiredRole: Role;
  children: ReactNode;
}

interface State {
  status: 'loading' | 'signedOut' | 'forbidden' | 'ready';
  user: User | null;
  role: string | null;
}

export function AuthGate({ requiredRole, children }: Props) {
  const [state, setState] = useState<State>({ status: 'loading', user: null, role: null });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // onIdTokenChanged rather than onAuthStateChanged: it also fires when the
    // token refreshes, so a freshly granted role takes effect within the hour
    // without the user clearing site data.
    return onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setState({ status: 'signedOut', user: null, role: null });
        return;
      }
      const token = await user.getIdTokenResult();
      const role = (token.claims.role as string | undefined) ?? 'customer';
      setState({
        status: role === requiredRole ? 'ready' : 'forbidden',
        user,
        role,
      });
    });
  }, [requiredRole]);

  const handleSignIn = async () => {
    setError(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    }
  };

  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.status === 'signedOut') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h5">EPrintSmart</Typography>
          <Typography color="text.secondary">
            Sign in to continue to the {requiredRole} console.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="contained" onClick={handleSignIn}>
            Sign in with Google
          </Button>
        </Stack>
      </Box>
    );
  }

  if (state.status === 'forbidden') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 3 }}>
        <Stack spacing={2} alignItems="center" maxWidth={480}>
          <Alert severity="warning" sx={{ width: '100%' }}>
            This account does not have {requiredRole} access.
          </Alert>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Signed in as {state.user?.email} with the role &ldquo;{state.role}&rdquo;. Ask an
            administrator to grant access, then sign out and back in.
          </Typography>
          <Button onClick={() => signOut(auth)}>Sign out</Button>
        </Stack>
      </Box>
    );
  }

  return <>{children}</>;
}
